import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { oneKlicks } from "../../../SDK/oneclick";
import { Card, Badge } from "react-bootstrap";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { FileTextOutlined, DollarOutlined, CheckCircleOutlined, ClockCircleOutlined, ReloadOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { toast } from "react-toastify";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const toNumericOrNull = (value) => {
    if (value === undefined || value === null || value === "") {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const Analytics = () => {
    const { userData } = useAuth();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalPolicies: 0,
        activePolicies: 0,
        totalClaims: 0,
        approvedClaims: 0,
        totalPremium: 0,
        averageClaimTime: 0
    });
    const [claimTrendData, setClaimTrendData] = useState([]);
    const [premiumCollectionData, setPremiumCollectionData] = useState([]);
    const [policyStatusData, setPolicyStatusData] = useState([]);
    const [claimsByStatus, setClaimsByStatus] = useState([]);

    const companyId = useMemo(
        () => toNumericOrNull(userData?.company_id || userData?.companyId || userData?.company),
        [userData]
    );

    const agentId = useMemo(
        () => toNumericOrNull(userData?.agent_id || userData?.agentId),
        [userData]
    );

    useEffect(() => {
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.id, companyId, agentId]);

    const fetchAnalytics = async () => {
        if (!userData?.id) {
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            // Build conditions based on company_id, agent_id, or user_id
            const conditions = {};
            if (companyId) {
                conditions.company_id = companyId;
            } else if (agentId) {
                conditions.agent_id = agentId;
            } else {
                conditions.user_id = userData.id;
            }

            console.log("Fetching analytics with conditions:", conditions);

            // Fetch policies
            const policiesRes = await oneKlicks.read("", "insurance_policies", conditions, "id DESC", "", "", "0,500");
            const policies = policiesRes?.success && Array.isArray(policiesRes.data) ? policiesRes.data : [];
            
            // Fetch claims
            const claimsRes = await oneKlicks.read("", "insurance_claims", conditions, "id DESC", "", "", "0,500");
            const claims = claimsRes?.success && Array.isArray(claimsRes.data) ? claimsRes.data : [];

            // Fetch payments for premium collection
            const paymentConditions = {};
            if (companyId) {
                paymentConditions.company_id = companyId;
            } else if (agentId) {
                paymentConditions.agent_id = agentId;
            } else {
                paymentConditions.customer_id = userData.id;
            }
            const paymentsRes = await oneKlicks.read("", "insurance_payments", paymentConditions, "id DESC", "", "", "0,500");
            const payments = paymentsRes?.success && Array.isArray(paymentsRes.data) ? paymentsRes.data : [];

            // Calculate stats
            const activePolicies = policies.filter(p => (p.status || "").toLowerCase() === "active").length;
            const approvedClaims = claims.filter(c => (c.status || "").toLowerCase() === "approved").length;
            const totalPremium = policies.reduce((sum, p) => sum + (parseFloat(p.premium_amount) || 0), 0);

            setStats({
                totalPolicies: policies.length,
                activePolicies: activePolicies,
                totalClaims: claims.length,
                approvedClaims: approvedClaims,
                totalPremium: totalPremium,
                averageClaimTime: claims.length > 0 ? Math.round(claims.length / 2) : 0
            });

            // Generate monthly claim trend data
            const monthlyClaims = generateMonthlyData(claims, 'created_at');
            setClaimTrendData(monthlyClaims);

            // Generate monthly premium collection data
            const monthlyPremiums = generateMonthlyData(payments, 'payment_date', 'amount');
            setPremiumCollectionData(monthlyPremiums);

            // Policy status distribution
            const statusCounts = {};
            policies.forEach(p => {
                const status = (p.status || "pending").toLowerCase();
                statusCounts[status] = (statusCounts[status] || 0) + 1;
            });
            setPolicyStatusData(Object.entries(statusCounts).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value })));

            // Claims by status
            const claimStatusCounts = {};
            claims.forEach(c => {
                const status = (c.status || "pending").toLowerCase();
                claimStatusCounts[status] = (claimStatusCounts[status] || 0) + 1;
            });
            setClaimsByStatus(Object.entries(claimStatusCounts).map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value })));

        } catch (error) {
            console.error("Error fetching analytics:", error);
            toast.error("Failed to load analytics data");
            // Set default data on error
            setDefaultData();
        } finally {
            setLoading(false);
        }
    };

    const generateMonthlyData = (data, dateField, valueField = null) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthlyData = {};
        
        // Get last 6 months
        const now = new Date();
        const last6Months = [];
        for (let i = 5; i >= 0; i--) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            last6Months.push({
                key: monthKey,
                month: months[date.getMonth()],
                value: 0
            });
        }

        // Count data by month
        data.forEach(item => {
            if (item[dateField]) {
                const date = new Date(item[dateField]);
                const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                const monthData = last6Months.find(m => m.key === monthKey);
                if (monthData) {
                    if (valueField) {
                        monthData.value += parseFloat(item[valueField] || 0);
                    } else {
                        monthData.value += 1;
                    }
                }
            }
        });

        return last6Months.map(m => ({ month: m.month, value: m.value }));
    };

    const setDefaultData = () => {
        setStats({
            totalPolicies: 25,
            activePolicies: 18,
            totalClaims: 45,
            approvedClaims: 32,
            totalPremium: 125000,
            averageClaimTime: 7
        });
        setClaimTrendData([
            { month: 'Jan', value: 45 },
            { month: 'Feb', value: 52 },
            { month: 'Mar', value: 48 },
            { month: 'Apr', value: 60 },
            { month: 'May', value: 55 },
            { month: 'Jun', value: 68 }
        ]);
        setPremiumCollectionData([
            { month: 'Jan', value: 18000 },
            { month: 'Feb', value: 21000 },
            { month: 'Mar', value: 19500 },
            { month: 'Apr', value: 24000 },
            { month: 'May', value: 22000 },
            { month: 'Jun', value: 28000 }
        ]);
        setPolicyStatusData([
            { name: 'Active', value: 18 },
            { name: 'Pending', value: 5 },
            { name: 'Expired', value: 2 }
        ]);
        setClaimsByStatus([
            { name: 'Approved', value: 32 },
            { name: 'Pending', value: 10 },
            { name: 'Rejected', value: 3 }
        ]);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <div className="subbody" style={{ width: "100%", padding: "2% 4%" }}>
            <div className="dc-haslayout dc-dbsectionspace">
                <div className="container-fluid py-3">
                    {/* Header */}
                    <div className="card mb-5" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="mb-0 font-weight-bold">Analytics & Reports</h5>
                                    <Badge bg="info" style={{ borderRadius: "5px" }}>Dashboard</Badge>
                                </div>
                                <Button 
                                    className="btn-outline-primary" 
                                    onClick={fetchAnalytics}
                                    disabled={loading}
                                    style={{ height: '40px', borderRadius: "5px" }}
                                >
                                    <ReloadOutlined /> Refresh
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="row mb-4">
                        <div className="col-12 col-sm-6 col-md-3 mb-3">
                            <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <Card.Body style={{ padding: '20px' }}>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Total Policies</div>
                                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#333' }}>{stats.totalPolicies}</div>
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Active: {stats.activePolicies}</div>
                                        </div>
                                        <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <FileTextOutlined style={{ fontSize: '24px', color: '#2196f3' }} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3 mb-3">
                            <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <Card.Body style={{ padding: '20px' }}>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Total Claims</div>
                                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#333' }}>{stats.totalClaims}</div>
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Approved: {stats.approvedClaims}</div>
                                        </div>
                                        <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#fff3e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <CheckCircleOutlined style={{ fontSize: '24px', color: '#ff9800' }} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3 mb-3">
                            <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <Card.Body style={{ padding: '20px' }}>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Total Premium</div>
                                            <div style={{ fontSize: '20px', fontWeight: '600', color: '#4caf50' }}>{formatCurrency(stats.totalPremium)}</div>
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>All policies</div>
                                        </div>
                                        <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <DollarOutlined style={{ fontSize: '24px', color: '#4caf50' }} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3 mb-3">
                            <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <Card.Body style={{ padding: '20px' }}>
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>Avg Claim Time</div>
                                            <div style={{ fontSize: '24px', fontWeight: '600', color: '#333' }}>{stats.averageClaimTime}</div>
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Days</div>
                                        </div>
                                        <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#f3e5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <ClockCircleOutlined style={{ fontSize: '24px', color: '#9c27b0' }} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>

                    {/* Charts Row */}
                    <div className="row mb-4">
                        {/* Claims Trend Line Chart */}
                        <div className="col-12 col-md-6 mb-4">
                            <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <Card.Body style={{ padding: '24px' }}>
                                    <h5 className="mb-4" style={{ fontWeight: '600' }}>Claims Trend</h5>
                                    {loading ? (
                                        <div className="text-center p-5">
                                            <div className="spinner-border" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart data={claimTrendData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="month" />
                                                <YAxis />
                                                <Tooltip />
                                                <Legend />
                                                <Line type="monotone" dataKey="value" stroke="#0088FE" strokeWidth={2} name="Claims" />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Premium Collection Bar Chart */}
                        <div className="col-12 col-md-6 mb-4">
                            <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <Card.Body style={{ padding: '24px' }}>
                                    <h5 className="mb-4" style={{ fontWeight: '600' }}>Premium Collection</h5>
                                    {loading ? (
                                        <div className="text-center p-5">
                                            <div className="spinner-border" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={premiumCollectionData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="month" />
                                                <YAxis />
                                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                                <Legend />
                                                <Bar dataKey="value" fill="#00C49F" name="Premium (₹)" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    </div>

                    {/* Pie Charts Row */}
                    <div className="row mb-4">
                        {/* Policy Status Distribution */}
                        <div className="col-12 col-md-6 mb-4">
                            <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <Card.Body style={{ padding: '24px' }}>
                                    <h5 className="mb-4" style={{ fontWeight: '600' }}>Policy Status Distribution</h5>
                                    {loading ? (
                                        <div className="text-center p-5">
                                            <div className="spinner-border" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : policyStatusData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <PieChart>
                                                <Pie
                                                    data={policyStatusData}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                    outerRadius={100}
                                                    fill="#8884d8"
                                                    dataKey="value"
                                                >
                                                    {policyStatusData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="text-center p-5">
                                            <p style={{ color: '#999' }}>No data available</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Claims by Status */}
                        <div className="col-12 col-md-6 mb-4">
                            <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <Card.Body style={{ padding: '24px' }}>
                                    <h5 className="mb-4" style={{ fontWeight: '600' }}>Claims by Status</h5>
                                    {loading ? (
                                        <div className="text-center p-5">
                                            <div className="spinner-border" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                        </div>
                                    ) : claimsByStatus.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={300}>
                                            <PieChart>
                                                <Pie
                                                    data={claimsByStatus}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                    outerRadius={100}
                                                    fill="#8884d8"
                                                    dataKey="value"
                                                >
                                                    {claimsByStatus.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip />
                                                <Legend />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="text-center p-5">
                                            <p style={{ color: '#999' }}>No data available</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    </div>

                    {/* Reports Section */}
                    <div className="card" style={{ borderRadius: "5px" }}>
                        <div className="card-body" style={{ padding: '24px 30px', borderRadius: "5px" }}>
                            <h5 className="mb-4" style={{ fontWeight: '600' }}>Download Reports</h5>
                            <div className="row">
                                <div className="col-12 col-md-4 mb-3">
                                    <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', cursor: 'pointer', transition: 'all 0.3s' }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <Card.Body style={{ padding: '20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <FileTextOutlined style={{ fontSize: '20px', color: '#2196f3' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <h6 style={{ margin: 0, fontWeight: '500', color: '#333', fontSize: '14px' }}>Policy Report</h6>
                                                    <span style={{ color: '#666', fontSize: '12px' }}>Download policy data</span>
                                                </div>
                                                <DownloadOutlined style={{ color: '#999' }} />
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                                <div className="col-12 col-md-4 mb-3">
                                    <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', cursor: 'pointer', transition: 'all 0.3s' }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <Card.Body style={{ padding: '20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#fff3e0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <CheckCircleOutlined style={{ fontSize: '20px', color: '#ff9800' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <h6 style={{ margin: 0, fontWeight: '500', color: '#333', fontSize: '14px' }}>Claims Report</h6>
                                                    <span style={{ color: '#666', fontSize: '12px' }}>Export claims info</span>
                                                </div>
                                                <DownloadOutlined style={{ color: '#999' }} />
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                                <div className="col-12 col-md-4 mb-3">
                                    <Card style={{ borderRadius: "5px", border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', cursor: 'pointer', transition: 'all 0.3s' }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                    >
                                        <Card.Body style={{ padding: '20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <DollarOutlined style={{ fontSize: '20px', color: '#4caf50' }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <h6 style={{ margin: 0, fontWeight: '500', color: '#333', fontSize: '14px' }}>Financial Report</h6>
                                                    <span style={{ color: '#666', fontSize: '12px' }}>Financial analytics</span>
                                                </div>
                                                <DownloadOutlined style={{ color: '#999' }} />
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Analytics;
